// Form validation utility functions
import { isIndia } from '../../../utils/countries';

const normalizeWebsiteUrl = (value) => {
  const v = String(value || '').trim();
  if (!v) return '';
  return /^https?:\/\//i.test(v) ? v : `https://${v}`;
};

// Real-time per-field validation
export const validateField = (field, value, formData = {}) => {
  switch (field) {
    case 'companyName':
      if (!value) return 'Company name is required';
      return null;
    case 'countryOrigin':
      if (!value) return 'Country of origin is required';
      return null;
    case 'contactPersonName':
      if (!value) return 'Name of person in charge is required';
      return null;
    case 'designation':
      if (!value) return 'Designation is required';
      return null;
    case 'email':
      if (!value) return 'Email is required';
      if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value)) return 'Please enter a valid email address';
      return null;
    case 'phoneNumber': {
      if (!value) return 'Phone number is required';
      if (isIndia(formData.countryOrigin)) {
        const digits = String(value || '').replace(/\D/g, '');
        if (digits.length !== 10) return 'Phone number must be exactly 10 digits for India';
      }
      return null;
    }
    case 'website':
      if (!value) return null;
      if (!/^https?:\/\/.+\..+/.test(normalizeWebsiteUrl(value)))
        return 'Please enter a valid website URL (e.g., https://www.example.com)';
      return null;
    case 'yearEstablished': {
      if (!value) return null;
      const year = parseInt(value);
      const currentYear = new Date().getFullYear();
      if (isNaN(year) || year < 1800 || year > currentYear) return `Please enter a valid year between 1800 and ${currentYear}`;
      return null;
    }
    case 'registeredAddress':
      if (!value) return 'Registered address is required';
      return null;
    case 'registeredCity':
      if (!value) return 'Registered city is required';
      return null;
    case 'registeredState':
      if (!value) return 'Registered state is required';
      return null;
    case 'registeredDistrict':
      if (!value || !String(value).trim()) return 'Registered district is required';
      if (String(value).trim().length < 2) return 'Registered district must be at least 2 characters';
      return null;
    case 'registeredCountry':
      if (!value) return 'Registered country is required';
      return null;
    case 'registeredPincode':
      if (!value) return 'Registered pincode is required';
      if (isIndia(formData.countryOrigin) && !/^[1-9][0-9]{5}$/.test(value)) return 'Please enter a valid 6-digit PIN code';
      return null;
    case 'supplyAddress':
      if (!value) return 'Supply address is required';
      return null;
    case 'supplyCity':
      if (!value) return 'Supply city is required';
      return null;
    case 'supplyState':
      if (!value) return 'Supply state is required';
      return null;
    case 'supplyDistrict':
      if (!value || !String(value).trim()) return 'Supply district is required';
      if (String(value).trim().length < 2) return 'Supply district must be at least 2 characters';
      return null;
    case 'supplyCountry':
      if (!value) return 'Supply country is required';
      return null;
    case 'supplyPincode':
      if (!value) return 'Supply pincode is required';
      if (isIndia(formData.countryOrigin) && !/^[1-9][0-9]{5}$/.test(value)) return 'Please enter a valid 6-digit PIN code';
      return null;
    case 'bankName':
      if (!value) return 'Bank name is required';
      return null;
    case 'accountNumber':
      if (!value) return 'Account number is required';
      return null;
    case 'confirmAccountNumber':
      if (!value) return 'Confirm account number is required';
      if (formData.accountNumber && value !== formData.accountNumber) return 'Account number and confirm account number must match';
      return null;
    case 'accountType':
      if (!value) return 'Account type is required';
      return null;
    case 'bankAddress':
      if (!value) return 'Bank address is required';
      return null;
    case 'ifscCode':
      if (!isIndia(formData.countryOrigin)) return null;
      if (!value) return 'IFSC code is required';
      if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(value)) return 'Invalid IFSC code format';
      return null;
    case 'swiftCode':
      if (isIndia(formData.countryOrigin)) return null;
      if (!value) return 'Swift code is required for foreign vendors';
      if (!/^[A-Z0-9]{8,11}$/.test(value)) return 'Swift code must be 8-11 characters and contain only letters and numbers';
      return null;
    case 'preferredCurrency':
      if (!value) return 'Preferred currency is required';
      return null;
    case 'msmeStatus':
      if (isIndia(formData.countryOrigin) && !value) return 'MSME status is required';
      return null;
    case 'msmeCategory':
      if (isIndia(formData.countryOrigin) && formData.msmeStatus === 'registered' && !value) return 'MSME category is required';
      return null;
    case 'msmeNumber':
      if (isIndia(formData.countryOrigin) && formData.msmeStatus === 'registered') {
        if (!value) return 'UDYAM registration number is required';
        if (!/^UDYAM-[A-Z]{2}-[0-9]{2}-[0-9]{7}$/.test(value)) return 'UDYAM number must follow format UDYAM-XX-00-XXXXXXX (e.g., UDYAM-MH-00-1234567)';
      }
      return null;
    case 'classificationYear':
      if (isIndia(formData.countryOrigin) && formData.msmeStatus === 'registered') {
        if (!value) return 'Classification year is required';
        const year = parseInt(value);
        const currentYear = new Date().getFullYear();
        if (isNaN(year) || year < 2020 || year > currentYear) {
          return `Please enter a valid classification year between 2020 and ${currentYear}`;
        }
      }
      return null;
    case 'classificationDate':
      if (isIndia(formData.countryOrigin) && formData.msmeStatus === 'registered') {
        if (!value) return 'Classification date is required';
        const date = new Date(value);
        const currentDate = new Date();
        if (isNaN(date.getTime()) || date > currentDate) {
          return 'Please enter a valid classification date (not in the future)';
        }
      }
      return null;
    case 'panNumber':
      if (isIndia(formData.countryOrigin)) {
        if (!value) return 'PAN number is required';
        if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(value)) return 'Invalid PAN number format';
      }
      return null;
    case 'gstNumber':
      if (isIndia(formData.countryOrigin)) {
        if (!value) return 'GST number is required';
        if (!/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(value)) return 'Invalid GST number format';
      }
      return null;
    case 'gtaRegistration':
      if (!value) return 'GTA (Goods Transport Agency) registration status is required';
      return null;
    default:
      return null;
  }
};

export const validateStep = (step, formData, keepExistingDocuments = {}) => {
  const errors = {};

  switch (step) {
    case 1: // Company Information
      if (!formData.businessVertical) errors.businessVertical = 'Business vertical is required';
      if (!formData.companyName) errors.companyName = 'Company name is required';
      if (!formData.countryOrigin) errors.countryOrigin = 'Country of origin is required';
      
      // Conditional validation based on country
      if (isIndia(formData.countryOrigin)) {
    // Registration number no longer required for India; field removed from UI
      } else {
        if (!formData.incorporationCertificate) {
          errors.incorporationCertificate = 'Company incorporation certificate is required';
        } else {
          const incorpCertError = validateFileField(formData.incorporationCertificate, 'Company incorporation certificate', 10 * 1024 * 1024, ['application/pdf']);
          if (incorpCertError) errors.incorporationCertificate = incorpCertError;
        }
      }
      
      if (!formData.contactPersonName) errors.contactPersonName = 'Name of person in charge is required';
      
      if (!formData.email) errors.email = 'Email is required';
      else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.email)) {
        errors.email = 'Please enter a valid email address';
      }
      
      // Phone number validation - 10 digits for India only
      if (!formData.phoneNumber) errors.phoneNumber = 'Phone number is required';
      else if (isIndia(formData.countryOrigin)) {
        // For India, validate exactly 10 digits (excluding +91)
        const phoneDigits = formData.phoneNumber.replace(/\D/g, '');
        if (phoneDigits.length !== 10) {
          errors.phoneNumber = 'Phone number must be exactly 10 digits for India';
        }
      }
      
      // Website URL validation (optional field)
      if (formData.website && !/^https?:\/\/.+\..+/.test(normalizeWebsiteUrl(formData.website))) {
        errors.website = 'Please enter a valid website URL (e.g., https://www.example.com)';
      }
      
      // Year of establishment validation (optional field)
      if (formData.yearEstablished) {
        const year = parseInt(formData.yearEstablished);
        const currentYear = new Date().getFullYear();
        if (isNaN(year) || year < 1800 || year > currentYear) {
          errors.yearEstablished = `Please enter a valid year between 1800 and ${currentYear}`;
        }
      }
      break;

    case 2: // Address Details
      if (!formData.registeredAddress) errors.registeredAddress = 'Registered address is required';
      if (!formData.registeredCity) errors.registeredCity = 'Registered city is required';
      if (!formData.registeredState) errors.registeredState = 'Registered state is required';
      if (!formData.registeredDistrict?.trim()) errors.registeredDistrict = 'Registered district is required';
      else if (formData.registeredDistrict.trim().length < 2) {
        errors.registeredDistrict = 'Registered district must be at least 2 characters';
      }
      if (!formData.registeredCountry) errors.registeredCountry = 'Registered country is required';
      if (!formData.registeredPincode) errors.registeredPincode = 'Registered pincode is required';
      else if (isIndia(formData.countryOrigin) && !/^[1-9][0-9]{5}$/.test(formData.registeredPincode)) {
        errors.registeredPincode = 'Please enter a valid 6-digit PIN code';
      }
      
      if (!formData.supplyAddress) errors.supplyAddress = 'Supply address is required';
      if (!formData.supplyCity) errors.supplyCity = 'Supply city is required';
      if (!formData.supplyState) errors.supplyState = 'Supply state is required';
      if (!formData.supplyDistrict?.trim()) errors.supplyDistrict = 'Supply district is required';
      else if (formData.supplyDistrict.trim().length < 2) {
        errors.supplyDistrict = 'Supply district must be at least 2 characters';
      }
      if (!formData.supplyCountry) errors.supplyCountry = 'Supply country is required';
      if (!formData.supplyPincode) errors.supplyPincode = 'Supply pincode is required';
      else if (isIndia(formData.countryOrigin) && !/^[1-9][0-9]{5}$/.test(formData.supplyPincode)) {
        errors.supplyPincode = 'Please enter a valid 6-digit PIN code';
      }
      break;

    case 3: // Bank Information
      if (!formData.bankName) errors.bankName = 'Bank name is required';
      if (!formData.accountNumber) errors.accountNumber = 'Account number is required';
      if (!formData.confirmAccountNumber) errors.confirmAccountNumber = 'Confirm account number is required';
      
      // Validate that account number and confirm account number match
      if (formData.accountNumber && formData.confirmAccountNumber && 
          formData.accountNumber !== formData.confirmAccountNumber) {
        errors.confirmAccountNumber = 'Account number and confirm account number must match';
      }
      
      if (!formData.accountType) errors.accountType = 'Account type is required';
      if (!formData.bankAddress) errors.bankAddress = 'Bank address is required';
      if (!keepExistingDocuments.bankProof) {
        if (!formData.bankProof) {
          errors.bankProof = 'Bank proof document is required';
        } else {
          const bankProofError = validateFileField(formData.bankProof, 'Bank proof document');
          if (bankProofError) errors.bankProof = bankProofError;
        }
      }
      
      // IFSC code validation only for Indian banks
      if (isIndia(formData.countryOrigin)) {
        if (!formData.branchName) errors.branchName = 'Branch name is required';
        if (!formData.ifscCode) errors.ifscCode = 'IFSC code is required';
        else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifscCode)) {
          errors.ifscCode = 'Invalid IFSC code format';
        }
      } else {
        // Swift code validation for foreign vendors
        if (!formData.swiftCode) {
          errors.swiftCode = 'Swift code is required for foreign vendors';
        } else if (!/^[A-Z0-9]{8,11}$/.test(formData.swiftCode)) {
          errors.swiftCode = 'Swift code must be 8-11 characters and contain only letters and numbers';
        }
      }
      break;

    case 4: // Supplier Categorization (+ Preferred Currency)
      // Supplier type, supplier group, supplier category, and commodity code removed - collected in approval questionnaire
        if (!formData.preferredCurrency) errors.preferredCurrency = 'Preferred currency is required';
      
      // Annual turnover validation (optional field)
      if (formData.annualTurnover) {
        const turnover = parseFloat(formData.annualTurnover);
        if (isNaN(turnover) || turnover < 0) {
          errors.annualTurnover = 'Please enter a valid positive number for annual turnover';
        }
      }
      
      // Employee count validation (optional field)
      if (formData.employeeCount) {
        const employeeCount = parseInt(formData.employeeCount);
        if (isNaN(employeeCount) || employeeCount < 1) {
          errors.employeeCount = 'Please enter a valid positive number for employee count';
        }
      }
      
      // Annual turnover no longer required
      // Industry sector no longer required (Additional Classifications removed)
      // Products/services removed
      // Employee count no longer required (Additional Classifications removed)
      
      // Only validate MSME fields for Indian vendors
      if (isIndia(formData.countryOrigin)) {
        if (!formData.msmeStatus) errors.msmeStatus = 'MSME status is required';
        
        if (formData.msmeStatus === 'registered') {
          if (!formData.msmeCategory) errors.msmeCategory = 'MSME category is required';
          if (!formData.msmeNumber) errors.msmeNumber = 'UDYAM registration number is required';
          else if (!/^UDYAM-[A-Z]{2}-[0-9]{2}-[0-9]{7}$/.test(formData.msmeNumber)) {
            errors.msmeNumber = 'UDYAM number must follow format UDYAM-XX-00-XXXXXXX (e.g., UDYAM-MH-00-1234567)';
          }
          
          // New MSME classification fields validation - MANDATORY
          if (!formData.classificationYear) {
            errors.classificationYear = 'Classification year is required';
          } else {
            const year = parseInt(formData.classificationYear);
            const currentYear = new Date().getFullYear();
            if (isNaN(year) || year < 2020 || year > currentYear) {
              errors.classificationYear = `Please enter a valid classification year between 2020 and ${currentYear}`;
            }
          }
          
          if (!formData.classificationDate) {
            errors.classificationDate = 'Classification date is required';
          } else {
            const date = new Date(formData.classificationDate);
            const currentDate = new Date();
            if (isNaN(date.getTime()) || date > currentDate) {
              errors.classificationDate = 'Please enter a valid classification date (not in the future)';
            }
          }
          
          if (!formData.vendorType) errors.vendorType = 'Vendor type is required';
          
          if (!keepExistingDocuments.msmeCertificate) {
            if (!formData.msmeCertificate) {
              errors.msmeCertificate = 'MSME certificate is required';
            } else {
              const msmeCertError = validateFileField(formData.msmeCertificate, 'MSME certificate');
              if (msmeCertError) errors.msmeCertificate = msmeCertError;
            }
          }
        } else if (formData.msmeStatus === 'not-registered') {
          if (!keepExistingDocuments.msmeDeclarationFile) {
            if (!formData.msmeDeclaration) {
              errors.msmeDeclaration = 'MSME declaration is required';
            }
          }
        }
      }
      break;

    case 5: // Compliance (India only)
      // Tax registration number removed
      if (isIndia(formData.countryOrigin)) {
        if (!formData.panNumber) errors.panNumber = 'PAN number is required';
        else if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.panNumber)) {
          errors.panNumber = 'Invalid PAN number format';
        }
        if (!keepExistingDocuments.panDocument) {
          if (!formData.panDocument) {
            errors.panDocument = 'PAN document is required';
          } else {
            const panDocError = validateFileField(formData.panDocument, 'PAN document');
            if (panDocError) errors.panDocument = panDocError;
          }
        }
        
        if (!formData.gstNumber) errors.gstNumber = 'GST number is required';
        else if (!/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(formData.gstNumber)) {
          errors.gstNumber = 'Invalid GST number format';
        }
        if (!keepExistingDocuments.gstDocument) {
          if (!formData.gstDocument) {
            errors.gstDocument = 'GST document is required';
          } else {
            const gstDocError = validateFileField(formData.gstDocument, 'GST document');
            if (gstDocError) errors.gstDocument = gstDocError;
          }
        }
      } else {
        // For foreign vendors, entire step 5 is skipped; do not enforce these
      }
      
      // GTA (Goods Transport Agency) registration is required for both Indian and foreign vendors
      if (!formData.gtaRegistration) {
        errors.gtaRegistration = 'GTA (Goods Transport Agency) registration status is required';
      }
      break;

    case 6: // Agreements
      // Helper function to check if supplier is ODM
      const isODMSupplier = () => {
        return formData.supplierGroup === 'odm-amber';
      };

      // Helper function to check if supplier is Indian
      const isIndianSupplier = () => {
        return isIndia(formData.countryOrigin);
      };

      // Define which agreements should be shown and validated
      const agreementsToValidate = [
        { id: 'nda', shouldShow: () => !isODMSupplier() },
        { id: 'sqa', shouldShow: () => isIndianSupplier() },
        { id: 'fourM', shouldShow: () => true },
        { id: 'codeOfConduct', shouldShow: () => true },
        { id: 'complianceAgreement', shouldShow: () => isIndianSupplier() },
        { id: 'selfDeclaration', shouldShow: () => true },
        { id: 'overseasAgreement', shouldShow: () => !isIndianSupplier() }
      ];

      // Validate only the agreements that should be shown
      agreementsToValidate.forEach(agreement => {
        if (agreement.shouldShow() && !formData.agreements?.[agreement.id]) {
          errors[`agreements.${agreement.id}`] = 'This agreement must be accepted';
        }
      });

      // Digital Signature validation - MANDATORY for all vendors
      if (!keepExistingDocuments.digitalSignature) {
        if (!formData.digitalSignature) {
          errors.digitalSignature = 'Digital signature is required';
        } else {
          // Validate digital signature file
          const signatureError = validateFileField(
            formData.digitalSignature,
            'Digital signature',
            5 * 1024 * 1024, // Max 5MB
            ['image/jpeg', 'image/png'] // Only JPG and PNG allowed
          );
          if (signatureError) {
            errors.digitalSignature = signatureError;
          }
        }
        
        // Additional validation: check if file is actually an image
        if (formData.digitalSignature instanceof File) {
          const fileName = formData.digitalSignature.name.toLowerCase();
          if (!fileName.endsWith('.jpg') && !fileName.endsWith('.jpeg') && !fileName.endsWith('.png')) {
            errors.digitalSignature = 'Digital signature must be a JPG or PNG image file';
          }
        }
      }
      break;

    default:
      break;
  }

  return errors;
};

/** Full form validation for final submit (registration and regularization). */
export const validateAllStepsForSubmit = (formData, keepExistingDocuments = {}) => {
  const errors = {};
  if (isIndia(formData.countryOrigin)) {
    for (const step of [1, 2, 3, 4, 5, 6]) {
      Object.assign(errors, validateStep(step, formData, keepExistingDocuments));
    }
  } else {
    for (const step of [1, 2, 3, 4]) {
      Object.assign(errors, validateStep(step, formData, keepExistingDocuments));
    }
    Object.assign(errors, validateStep(6, formData, keepExistingDocuments));
  }
  return errors;
};

export const validateFileUpload = (file, maxSize = 10 * 1024 * 1024, allowedTypes = ['image/jpeg', 'image/png', 'application/pdf']) => {
  if (!file) return 'File is required';
  
  if (file.size > maxSize) {
    return `File size must be less than ${maxSize / (1024 * 1024)}MB`;
  }
  
  if (!allowedTypes.includes(file.type)) {
    return `File type must be one of: ${allowedTypes.join(', ')}`;
  }
  
  return null;
};

export const validateFileField = (file, fieldName, maxSize = 10 * 1024 * 1024, allowedTypes = ['image/jpeg', 'image/png', 'application/pdf']) => {
  if (!file) return `${fieldName} is required`;
  
  const validationError = validateFileUpload(file, maxSize, allowedTypes);
  if (validationError) {
    return validationError;
  }
  
  return null;
};

export const canProceedToNextStep = (step, formData, keepExistingDocuments = {}) => {
  const errors = validateStep(step, formData, keepExistingDocuments);
  return Object.keys(errors).length === 0;
};